<template>
  <div>
    <div class="d-flex align-items-center justify-content-end mb-3">
      <div>
        <b-button
          v-show="chartData"
          v-b-toggle.collapse-chart-sample
          variant="teal"
          class="mr-2"
          @click="viewSample = !viewSample"
        >
          <b-icon-bar-chart-fill></b-icon-bar-chart-fill>
          샘플 보기
        </b-button>

        <b-button
          variant="primary"
          @click="initChart"
          class="mr-2"
          :disabled="!chartData"
        >
          <b-icon-arrow-clockwise></b-icon-arrow-clockwise>
          리셋하기
        </b-button>
      </div>
    </div>
    <template v-if="chartData">
      <b-collapse id="collapse-chart-sample" class="mt-2 mb-3">
        <b-card>
          <p class="card-text">샘플을 적용해보세요</p>
          <b-button
            v-for="(chart_data, idx) in data_sample_list"
            :key="idx"
            size="sm"
            class="mr-2 mb-2"
            variant="outline-dark"
            @click="applySample(idx)"
          >
            {{ chart_data.label }}
          </b-button>
        </b-card>
      </b-collapse>
    </template>

    <template v-if="!chartData">
      <b-card header="차트 생성/편집">
        <div class="text-center p-3">
          <b-btn variant="primary" class="mr-3" @click="initChart"
            >신규 생성하기</b-btn
          >
          <b-btn
            variant="teal"
            v-show="!chartData"
            @click="viewSample = !viewSample"
            >샘플로 시작하기
          </b-btn>
        </div>

        <b-collapse
          v-model="viewSample"
          id="collapse-chart-sample"
          class="mt-3"
        >
          <b-card>
            <p class="card-text text-center fw-500">샘플을 적용해보세요!</p>
            <b-button
              v-for="(chart_data, idx) in data_sample_list"
              :key="`chart-sample-${idx}`"
              size="sm"
              class="mr-2 mb-2"
              variant="outline-dark"
              @click="applySample(idx)"
            >
              {{ chart_data.label }}
            </b-button>
          </b-card>
        </b-collapse>
      </b-card>
    </template>

    <template v-else>
      <canvas
        :style="'max-height: ' + chartData?.options?.chart_height + 'px;'"
        ref="myChart"
        class="border border-light shadow-sm rounded"
      ></canvas>
      <small v-if="chartData && chartData.options" class="text-muted p-4"  style="white-space: pre-line">
        {{ chartData.options.footnote }}
      </small>

      <b-card no-body class="mt-3">
        <b-tabs card>
          <b-tab title="차트 데이터 편집" active>
            <b-row>
              <b-col cols="12">
                <EditChartDataTable
                  :chartData="chartData.data"
                  @updateChartData="updateChartData"
                />
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="차트 편집" active>
            <b-row>
              <b-col cols="12">
                <EditChartGlobalConfig
                  :labels="chartData.data.labels"
                  :options="{ ...chartData.options }"
                  :datasets="[...chartData.data.datasets]"
                  @updateChartData="updateChartData"
                  @updateChartOption="updateChartOption"
                  @checkDuplicatedTitle="checkDuplicatedTitle"
                />
              </b-col>
            </b-row>
          </b-tab>

          <b-tab title="차트 세부 설정">
            <EditChartLocalConfig
              :datasets="[...chartData.data.datasets]"
              :options="{ ...chartData.options }"
              :labels="{ ...chartData.data.labels }"
              @updateChart="updateChart"
              @updateChartDataSets="updateChartDataSets"
            />
          </b-tab>
        </b-tabs>
      </b-card>
    </template>

    <div class="pt-5 pb-3">
      <b-button variant="teal" @click="saveChart" v-if="chart">
        저장하기
      </b-button>
      <b-button @click="closeEditChartModal" class="ml-3">
        리스트로 돌아가기
      </b-button>
    </div>
  </div>
</template>

<script>
  import { mapState, mapGetters, mapActions, mapMutations } from 'vuex';
  import Chart from 'chart.js/auto';
  import { data_skelton_single } from '@/util/chart/chart.utils.js';
  import { data_sample_list } from '@/util/chart/chart.sample.skelton.js';
  import EditChartDataTable from '@/components/writing_report/chart_subs/EditChartDataTable.vue';
  import EditChartGlobalConfig from './EditChartGlobalConfig.vue';
  import EditChartLocalConfig from './EditChartLocalConfig.vue';
  import { multiChart } from './chartSample';
  import cloneDeep from 'lodash/cloneDeep';
  export default {
    name: 'DefaultChart',
    components: {
      EditChartDataTable,
      EditChartGlobalConfig,
      EditChartLocalConfig,
    },
    props: {
      editing_mode: { type: Boolean, default: () => false },
      e_mail: { type: String, default: '' },
    },
    computed: {
      ...mapState('report_list', ['report_code']),
      ...mapGetters('story_board', ['selected_lv2']),
      ...mapState('charts', [
        'chart_data',
        'selected_chart_id',
        'selected_chart_data',
      ]),
    },
    data() {
      return {
        multiChart,
        data_sample_list,
        chart: null,
        chartData: null,
        initialData: { ...data_skelton_single },
        viewSample: true,
        usable_title: false,
      };
    },
    watch: {
      'chartData.options.chart_height': function (newVal, oldVal) {
        this.$nextTick(() => {
          this.renderChart();
        });
      },
    },
    mounted() {
      if (this.selected_chart_data !== null) {
        this.initialData = JSON.parse(JSON.stringify(this.selected_chart_data));
        this.initChart();
        return;
      }

      if (this.selected_chart_id) {
        this.initialData = {
          ...this.chart_data.find(
            (obj) => obj.chart_id === this.selected_chart_id
          )['form'],
        };
        this.initChart();
        return;
      }
    },
    beforeDestroy() {
      if (this.chart) {
        this.chart.destroy();
        this.chart = null;
      }
      this.chartData = null;
    },
    methods: {
      ...mapActions('story_board', ['addErtContent']),
      ...mapActions('charts', ['saveAddChart']),
      ...mapMutations('charts', ['update_edited_chart_id']),
      initChart() {
        this.chartData = JSON.parse(JSON.stringify(this.initialData));

        this.$nextTick(() => {
          this.renderChart();
        });
      },
      updateChartOption(options) {
        this.chartData.options = {
          ...this.chartData.options,
          ...options,
        };

        this.chartData.label = this.chartData.options?.plugins?.title?.text ?? '';
        if (this.chart && this.chart.options) {
          if (this.chart) {
            this.chart.destroy();
          }
          const ctx = this.$refs.myChart.getContext('2d');
          this.chart = new Chart(ctx, cloneDeep(this.chartData));
        }
      },
      updateChartDataSets(_datasets) {
        this.chartData.data.datasets = _datasets;

        this.chart.data.datasets = this.chartData.data.datasets;
        this.chart.update();
      },
      updateChartData({ labels, datasets }) {
        this.chartData.data.labels = labels;
        this.chartData.data.datasets = datasets;
        this.chart.update();
      },
      updateChart({ data, options }) {
        if (this.chart) {
          this.chart.destroy();
        }

        this.chartData.data = { ...this.chartData.data, ...data };
        this.chartData.options = { ...this.chartData.options, ...options };

        const ctx = this.$refs.myChart.getContext('2d');
        this.chart = new Chart(ctx, this.chartData);
      },
      renderChart() {
        if (!this.chartData) return;
        this.chartData.options = this.chartData.options || {};
        this.chartData.options.plugins = this.chartData.options.plugins || {};
        
        this.chartData.plugins = [
          {
            id: 'customCanvasBackgroundColor',
            beforeDraw: (chart, args, options) => {
              const { ctx } = chart;
              ctx.save();
              ctx.globalCompositeOperation = 'destination-over';
              ctx.fillStyle = options.color || 'white';
              ctx.fillRect(0, 0, chart.width, chart.height);
              ctx.restore();
            },
          },
        ];

        this.chartData.options.plugins['tooltip'] = {
          intersect: true,
          callbacks: {
            label: (context) => {
              return context.parsed.y !== undefined
                ? `${context.dataset.label}: ${context.parsed.y.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (단위: ${context.dataset?.unit})`
                : `${context.dataset.label}: ${context.dataset.label}: ${context.parsed.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} (단위: ${context.dataset?.unit})`;
            },
          },
        };

        if (this.chart) {
          this.chart.destroy();
        }

        const ctx = this.$refs.myChart.getContext('2d');
        this.chart = new Chart(ctx, this.chartData);
      },
      applySample(idx) {
        this.chartData = JSON.parse(JSON.stringify(this.data_sample_list[idx]));

        this.$nextTick(() => {
          this.renderChart();
        });
      },
      closeEditChartModal() {
        if (this.editing_mode) {
          this.$emit('flag_modal_mode', 'chart_list');
        } else {
          this.$emit('closeEditChartModal');
        }
      },

      async saveChart() {
        try {
          const result = await this.saveAddChart({
            report_code: this.report_code,
            payload: this.chartData,
            chart_id: this.selected_chart_id,
            e_mail: this.e_mail,
          });

          this.update_edited_chart_id(this.selected_chart_id);

          if (this.$route.name === 'WriteStoryBoard' && !this.selected_chart_id) {
            this.addErtContent({
              report_code: this.report_code,
              lv2_key: this.selected_lv2.key,
              id: result.chart_id,
              type: 'chart',
            });

          }
          this.$emit('closeModal', this.chartData);
          this.$emit('closeEditChartModal', this.chartData);
        } catch (error) {
          this.$bvToast.toast('차트 저장 중 오류가 발생했습니다.', {
            title: '오류',
            variant: 'danger',
            solid: true,
            toaster: 'b-toaster-bottom-right',
            toastClass: 'custom-toast', 
            appendToast: true,
          });
          error.message = '[차트 저장]' + error.message;
          this.$Sentry.captureException(error);
        }
      },
      async checkDuplicatedTitle(input_title) {
        const response = await axios.get(
          `${this.$store.state.backend_host}/checkDuplicatedChartTitle?rc=${this.report_code}&title=${input_title}&chart_id=${this.selected_chart_id}`
        );
        if (response.data.result && input_title.length > 0) {
          this.usable_title = true;
          setTimeout(() => {
            this.usable_title = null;
          }, 5000);
        } else {
          this.usable_title = false;
        }
      },
    },
  };
</script>

<style lang="scss">
  .btn.is-active {
    background-color: rgba(0, 0, 0, 0.1);
  }
</style>
